import { useCompare } from '../../context/CompareContext';
import { productDetails, getGenericSpecs } from '../../mocks/productDetails';
import SafeImage from '../base/SafeImage';

export default function CompareModal() {
  const { compareItems, clearCompare, closeCompareModal } = useCompare();

  const allSpecGroups = compareItems.flatMap((p) => {
    const detail = productDetails[String(p.id)];
    return detail?.specs ?? getGenericSpecs(p);
  });

  const groupNames = [...new Set(allSpecGroups.map((g) => g.group))];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[65]"
        style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' }}
        onClick={closeCompareModal}
      />

      {/* Modal — always dark */}
      <div className="fixed z-[66] inset-0 flex items-center justify-center p-4 sm:p-8 pointer-events-none">
        <div
          className="pointer-events-auto w-full flex flex-col overflow-hidden rounded-3xl"
          style={{
            maxWidth: '960px',
            maxHeight: 'calc(100vh - 4rem)',
            minHeight: '400px',
            background: '#ffffff',
            border: '1px solid rgba(0,0,0,0.12)',
            boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 sm:px-8 py-5 flex-shrink-0"
            style={{ borderBottom: '1px solid rgba(0,0,0,0.096)', background: '#ffffff' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ background: 'rgba(228,27,29,0.18)', border: '1px solid rgba(228,27,29,0.35)' }}
              >
                <i className="ri-swap-line text-red-400 text-base" />
              </div>
              <div>
                <h2 className="font-extrabold text-sm uppercase tracking-widest" style={{ color: '#1f1f1f' }}>
                  Comparación de Productos
                </h2>
                <p className="text-[10px] mt-0.5" style={{ color: '#52525b' }}>
                  {compareItems.length} producto{compareItems.length !== 1 ? 's' : ''} seleccionado{compareItems.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={clearCompare}
                className="text-xs uppercase tracking-wider font-bold transition-colors cursor-pointer whitespace-nowrap hover:text-red-400"
                style={{ color: '#52525b' }}
              >
                Limpiar todo
              </button>
              <button
                onClick={closeCompareModal}
                className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all hover:bg-white/10"
                style={{ background: 'rgba(0,0,0,0.072)', border: '1px solid rgba(0,0,0,0.12)' }}
              >
                <i className="ri-close-line text-zinc-400 text-lg" />
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-auto" style={{ background: '#ffffff' }}>
            <table
              className="w-full border-collapse"
              style={{ minWidth: `${compareItems.length * 220 + 160}px` }}
            >
              {/* Product headers */}
              <thead>
                <tr>
                  <th
                    className="w-40 p-5 text-left sticky top-0 z-10"
                    style={{ background: '#ffffff', borderBottom: '1px solid rgba(0,0,0,0.096)' }}
                  >
                    <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#52525b' }}>
                      Especificación
                    </span>
                  </th>

                  {compareItems.map((product, idx) => (
                    <th
                      key={product.id}
                      className="p-5 sticky top-0 z-10 min-w-[200px]"
                      style={{
                        background: '#ffffff',
                        borderBottom: '1px solid rgba(0,0,0,0.096)',
                        borderLeft: idx === 0 ? '1px solid rgba(0,0,0,0.072)' : undefined,
                      }}
                    >
                      <div className="flex flex-col items-center gap-3">
                        <div
                          className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-white"
                          style={{ border: '1px solid rgba(0,0,0,0.12)' }}
                        >
                          <SafeImage src={product.images?.[0]} alt={product.name} className="w-full h-full object-contain p-1" />
                        </div>
                        <p className="text-xs font-semibold text-center line-clamp-2 leading-snug" style={{ color: '#27272a' }}>
                          {product.name}
                        </p>
                        {product.price && (
                          <span
                            className="text-sm font-extrabold px-3 py-1 rounded-full"
                            style={{ background: 'rgba(228,27,29,0.20)', color: '#c3161a' }}
                          >
                            RD${product.price.toLocaleString('es-DO')}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Spec rows */}
              {groupNames.length === 0 ? (
                <tbody>
                  <tr>
                    <td
                      colSpan={compareItems.length + 1}
                      className="px-5 py-12 text-center"
                    >
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-zinc-800 flex items-center justify-center">
                        <i className="ri-file-list-3-line text-zinc-500 text-xl" />
                      </div>
                      <p className="text-zinc-400 text-sm font-medium">Sin especificaciones para comparar</p>
                      <p className="text-zinc-600 text-xs mt-1">Las especificaciones técnicas se mostrarán cuando el backend las proporcione.</p>
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {groupNames.map((groupName) => {
                    const allSpecLabels = compareItems.flatMap((p) => {
                      const detail = productDetails[String(p.id)];
                      const specs = detail?.specs ?? getGenericSpecs(p);
                      return specs.find((g) => g.group === groupName)?.specs.map((s) => s.label) ?? [];
                    });
                    const uniqueLabels = [...new Set(allSpecLabels)];

                    return (
                      <>
                        {/* Group header */}
                        <tr key={`group-${groupName}`}>
                          <td
                            colSpan={compareItems.length + 1}
                            className="px-5 py-3"
                            style={{
                              background: 'rgba(228,27,29,0.08)',
                              borderTop: '1px solid rgba(228,27,29,0.20)',
                              borderBottom: '1px solid rgba(228,27,29,0.20)',
                            }}
                          >
                            <span className="text-red-400 text-[10px] font-extrabold uppercase tracking-widest">
                              {groupName}
                            </span>
                          </td>
                        </tr>

                        {/* Spec rows */}
                        {uniqueLabels.map((label, rowIdx) => (
                          <tr
                            key={`${groupName}-${label}`}
                            style={{ background: rowIdx % 2 === 0 ? 'rgba(0,0,0,0.03)' : 'transparent' }}
                          >
                            <td
                              className="px-5 py-3 text-xs font-semibold"
                              style={{ color: '#52525b', borderRight: '1px solid rgba(0,0,0,0.06)' }}
                            >
                              {label}
                            </td>
                            {compareItems.map((product) => {
                              const detail = productDetails[String(product.id)];
                              const specs = detail?.specs ?? getGenericSpecs(product);
                              const group = specs.find((g) => g.group === groupName);
                              const specItem = group?.specs.find((s) => s.label === label);
                              return (
                                <td
                                  key={product.id}
                                  className="px-5 py-3 text-center text-xs"
                                  style={{ borderRight: '1px solid rgba(0,0,0,0.06)' }}
                                >
                                  {specItem ? (
                                    <span
                                      className={specItem.highlight ? 'font-extrabold' : 'font-medium'}
                                      style={{ color: specItem.highlight ? '#c3161a' : '#3f3f46' }}
                                    >
                                      {specItem.value}
                                    </span>
                                  ) : (
                                    <span style={{ color: '#a1a1aa' }}>—</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>

          {/* Footer */}
          <div
            className="px-6 sm:px-8 py-4 flex items-center justify-end gap-3 flex-shrink-0"
            style={{ borderTop: '1px solid rgba(0,0,0,0.084)', background: '#ffffff' }}
          >
            <p className="text-xs flex-1 hidden sm:block" style={{ color: '#52525b' }}>
              Compara especificaciones técnicas lado a lado para elegir el producto ideal.
            </p>
            <button
              onClick={closeCompareModal}
              className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider cursor-pointer transition-all whitespace-nowrap hover:bg-white/10"
              style={{
                background: 'rgba(0,0,0,0.072)',
                color: '#52525b',
                border: '1px solid rgba(0,0,0,0.12)',
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
